import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { DateValueType, formatDate } from '@lib/utils.ts';
import {
  TReportBlacklistUsersParams,
  TReportUsersWithTopOfBook,
} from '@src/types';
import { useNavigate } from 'react-router-dom';
import { getUsersWithTopOfBook } from '@src/services/reports.ts';
import {
  Button,
  Card,
  DateRangePicker,
  Divider,
  RadixTable,
  TColumn,
} from '@features/ui';

export const UsersWithTopOfBook = () => {
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [data, setData] = useState<TReportUsersWithTopOfBook[]>([]);

  const navigate = useNavigate();

  const fetchUsersWithTopOfBook = async (
    params?: TReportBlacklistUsersParams
  ) => {
    const res = await getUsersWithTopOfBook(params);

    setData(res);
  };

  useEffect(() => {
    fetchUsersWithTopOfBook().then();
  }, []);

  const handleGetData = async () => {
    await fetchUsersWithTopOfBook({
      start_date: valueStart?.startDate
        ? format(valueStart.startDate, 'yyyy-MM-dd')
        : null,
      end_date: valueEnd?.startDate
        ? format(valueEnd.startDate, 'yyyy-MM-dd')
        : null,
    });
  };

  const renderLevel = (level?: boolean, depth?: boolean) => {
    if (!level) {
      return (
        <p
          className={
            'bg-[#ddd] py-[2px] px-1 text-[#ababab] rounded-[3px] w-fit'
          }
        >
          N/A
        </p>
      );
    }
    if (depth) {
      return (
        <p className={'bg-lime py-[2px] px-1 rounded-[3px] w-fit'}>LVL2</p>
      );
    }

    return <p className={'bg-lime py-[2px] px-1 rounded-[3px] w-fit'}>LVL1</p>;
  };

  const columns = useMemo(
    (): TColumn<TReportUsersWithTopOfBook>[] => [
      {
        title: 'Username',
        render: (item) => <p>{item?.username}</p>,
      },
      {
        title: 'Name',
        render: (item) => (
          <p>
            {item?.user.first_name} {item?.user.last_name}
          </p>
        ),
      },
      {
        title: 'Email',
        render: (item) => <p>{item?.user.email}</p>,
      },
      {
        title: 'Expiry',
        render: (item) => <p>{formatDate(item?.expire_date)}</p>,
      },
      {
        title: 'Accounts',
        render: (item) => (
          <p>
            Active: {item?.active ?? 0} | Closed: {item?.closed ?? 0}
          </p>
        ),
      },
      {
        title: 'CBOT',
        render: (item) => renderLevel(item?.cbot, item?.cbot_depth),
      },
      {
        title: 'CME',
        render: (item) => renderLevel(item?.cme, item?.cme_depth),
      },
      {
        title: 'COMEX',
        render: (item) => renderLevel(item?.comex, item?.comex_depth),
      },
      {
        title: 'NYMEX',
        render: (item) => renderLevel(item?.nymex, item?.nymex_depth),
      },
      {
        title: 'EUREX',
        render: (item) => renderLevel(item?.eurex, item?.eurex_depth),
      },
      {
        title: 'Q',
        render: (item) => renderLevel(item?.q, item?.q_depth),
      },
      {
        title: 'Max Sessions',
        render: (item) => <p>{item?.max_session_data}</p>,
      },
      {
        title: '',
        render: (item) => (
          <div>
            <Button
              size={'sm'}
              variant={'secondary'}
              onClick={() => navigate(`/admin/users/${item?.id}`)}
            >
              Edit
            </Button>
          </div>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div>
      <div className={'text-xs flex flex-col gap-x-8 gap-y-3'}>
        <h1
          className={'text-[23px]'}
        >{`Users with 'Top of Book' (level 2)`}</h1>
        {data?.length ? (
          <p>
            Viewing <b>{data?.length} user(s)</b>
          </p>
        ) : null}
      </div>
      <Card className={'mt-4.5'}>
        <div
          className={
            'flex justify-between items-start lg:flex-row flex-col mb-5 gap-6'
          }
        >
          <div className={'w-full flex gap-6 flex-wrap'}>
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created From'}
              size={'sm'}
              value={valueStart}
              variant={'outline'}
              onChange={setValueStart}
            />
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created To'}
              size={'sm'}
              value={valueEnd}
              variant={'outline'}
              onChange={setValueEnd}
            />
          </div>
          <div className={'flex gap-5'}>
            <Button size={'sm'} variant={'lime'} onClick={handleGetData}>
              Get
            </Button>
          </div>
        </div>
        <Divider />
        <RadixTable columns={columns} data={data} />
      </Card>
    </div>
  );
};
